/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';

//----------------------------------------------------------
// @CUSTOM: Added imports for additional config from
// extended definition
//----------------------------------------------------------
import classnames from 'classnames';
import { RichTextComponent } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';
//----------------------------------------------------------

const headerView: React.FC<IHeaderViewProps & IHeaderProps<{}>> = props => {    // @CUSTOM: Added extended definition props
    const {
        config,     // @CUSTOM: Get config from extended definition
        slots,      // @CUSTOM: Get slots from extended definition
        resources,  // @CUSTOM: Get resources from extended definition
        HeaderTag,
        HeaderContainer,
        HeaderTopBarContainer,
        MobileMenuContainer,
        MobileMenuBodyContainer,
        MobileMenuLinksContainer,
        // @CUSTOM: Removed 'Divider' as it is not used here
    } = props;

    //------------------------------------------------------
    // @CUSTOM: Separate sign-up link
    //------------------------------------------------------
    const signUpLink = !props.context.request.user.isAuthenticated ? (
        <Button
            className='ms-header__signup-button'
            title={props.resources.header__signUpLinkText}
            href={(props.config.signupOverride?.linkUrl.destinationUrl || props.context.request.user.signUpUrl) ?? ''}
            aria-label={props.resources.header__signUpLinkAriaText}
        >
            <span className='ms-header__signup-button-text'>
                {props.resources.header__signUpLinkText}
            </span>
        </Button>
    ): undefined;
    //------------------------------------------------------

    return (
        // @CUSTOM: Added className to tag below to conditionally add 'minimal' if minimal header is enabled
        <Module {...HeaderTag} className={classnames(HeaderTag.className, {'minimal': config.enableMinHeader})}>
            {/* @CUSTOM: TSX below has been reorganized for the theme, with some unnecessary elements removed */}
            <Node {...HeaderContainer}>
                <div className='ms-header__logo-wrapper'>
                    {props.logo}
                </div>
                {/* @CUSTOM: Do not render if minimal header is enabled */}
                {!config.enableMinHeader && (
                    <>
                        <Node {...HeaderTopBarContainer}>
                        <div className='ms-header__topbar-container'>
                            {config.bannerText &&
                                <div className='ms-header__banner'>
                                    <RichTextComponent className='ms-header__banner-text' text={config.bannerText} />
                                </div>
                            }
                            {_renderReactFragment(slots.secondaryMenuBar)}
                            <div className='ms-header__account'>
                                {/* @CUSTOM: Added prefix to signed in button, and only show when signed in */}
                                {props.context.request.user.isAuthenticated && <span className='ms-header__account-prefix'>{resources.header__signInLinkPrefix}</span>}
                                {/* @CUSTOM: Updated function below to include sign-up link in parameter */}
                                {_renderDesktopAccountBlock(props, signUpLink)}
                            </div>
                        </div>
                    </Node>
                    <div className='ms-header__bottombar'>
                        <div className='ms-header__bottombar-container'>
                            {_renderReactFragment(props.menuBar)}
                            {_renderReactFragment(props.search)}
                            {props.cartIcon}
                            {props.navIcon}
                        </div>
                    </div>
                    <Node {...MobileMenuContainer}>
                        <Node {...MobileMenuBodyContainer}>
                            {props.MobileMenuHeader}
                            {_renderReactFragment(props.menuBar)}
                            <Node {...MobileMenuLinksContainer}>
                                {props.accountLinks ? props.accountLinks.map(link => link) : false}
                                {/* @CUSTOM: Always provide sign out link if there is a sign in error */}
                                {props.context.request.user.retailServerErrorCode ? (
                                    <Button
                                        className='ms-header__signout-button'
                                        title={props.resources.signOutLinkText}
                                        href={props.context.request.user.signOutUrl ?? ''}
                                        aria-label={props.resources.signOutLinkAriaText}
                                    >
                                        <span className='ms-header__signout-button-text'>
                                            {props.resources.signOutLinkText}
                                        </span>
                                    </Button>
                                ) : (
                                    <>
                                        {props.signInLink}
                                        {/* @CUSTOM: Added separate sign-up link */}
                                        {signUpLink}
                                        {props.signOutLink}
                                    </>
                                )}
                            </Node>
                        </Node>
                    </Node>
                    </>
                )}
            </Node>
            {/*-------------------------------------------------------*/}
        </Module>
    );
};

function _renderDesktopAccountBlock(props: IHeaderViewProps, signUpLink?: JSX.Element): JSX.Element | null {    // @CUSTOM: Updated parameters to include sign-up link
    const {
        AccountInfoDropdownParentContainer,
        AccountInfoDropdownPopoverConentContainer,
        accountInfoDropdownButton,
        signOutLink,
        signInLink,
        accountLinks
    } = props;

    if (AccountInfoDropdownParentContainer) {
        if (AccountInfoDropdownPopoverConentContainer) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {accountInfoDropdownButton}
                    <Node {...AccountInfoDropdownPopoverConentContainer}>
                        {accountLinks ? accountLinks.map(link => link) : false}
                        {signOutLink}
                    </Node>
                </Node>
            );
        } else if (signInLink) {
            return (
                <Node {...AccountInfoDropdownParentContainer}>
                    {signInLink}
                    {/* @CUSTOM: Included separate sign-up link */}
                    {signUpLink}
                </Node>
            );
        }
    }
    props.context.telemetry.error('Header content is empty, module wont render.');
    return null;
}

function _renderReactFragment(items: React.ReactNode[]): JSX.Element | null {
    return (
        <>
            {items && items.length > 0
                ? items.map((slot: React.ReactNode, index: number) => {
                      return <React.Fragment key={index}>{slot}</React.Fragment>;
                  })
                : null}
        </>
    );
}

export default headerView;
